import { useEffect, useRef, useState } from "react";
import { clearContainer } from "./utils/dom";
import { Application, Container } from "pixi.js";
import { TextureList, loadTexturesFromSpritesheet } from "./game/Textures";
import { createPlayer } from "./game/Player";
import { createFire } from "./game/Fire";
import useFavicon from "@williamgrosset/react-use-favicon";
import { loadSound } from "./utils/sound";
import { track } from "@vercel/analytics";

const TOTAL_NPCS = 200;

type GameContainerParams = {
  screensaver?: boolean;
};

export default function GameContainer({ screensaver = false }) {
  const domRef = useRef<HTMLDivElement | null>(null);
  const [app, setApp] = useState<Application | null>(null);
  const [textures, setTextures] = useState<TextureList | null>(null);
  const { update: setFavIcon } = useFavicon();

  // boot up, never change me
  useEffect(() => {
    let isMounted = true;
    const a = new Application();

    (async () => {
      const el = domRef.current!;
      await a.init({
        antialias: true,
        width: 320,
        height: 240,
        resizeTo: window,
        backgroundColor: 0x001c95,
      });

      const t = await loadTexturesFromSpritesheet();
      await loadSound("/burn.wav");

      if (!isMounted) return;

      clearContainer(el);
      el.appendChild(a.canvas);
      setApp(a);
      setTextures(t);
    })();

    return () => {
      isMounted = false;
      if (domRef.current) {
        clearContainer(domRef.current);
      }
    };
  }, [domRef]);

  // app is ready
  useEffect(() => {
    if (!app || !textures) {
      return;
    }

    const npcs = new Array(TOTAL_NPCS).fill(1).map((_) =>
      createPlayer({
        isNPC: true,
        textures: screensaver ? textures.player : textures.npc,
        stickTextures: textures.sticks,
        screensaver,
      })
    );

    const player = createPlayer({
      isNPC: false,
      textures: textures.player,
      stickTextures: textures.sticks,
      screensaver,
    });

    const fire = createFire({ fire: textures.fire, base: textures.fireBase });

    const spriteContainer = new Container();
    const fireContainer = new Container();

    player.container.visible = screensaver === false;
    fireContainer.visible = screensaver === false;

    const favIcon = document.createElement("canvas");
    const favIconContext = favIcon.getContext("2d")!;
    favIcon.width = 32;
    favIcon.height = 32;

    app.stage.addChild(spriteContainer);
    app.stage.addChild(fireContainer);
    npcs.forEach((npc) => spriteContainer.addChild(npc.container));
    fireContainer.addChild(fire.container);
    spriteContainer.addChild(player.container);

    const handleResize = () => {
      const dw = document.documentElement.clientWidth;
      const dh = document.documentElement.clientHeight;

      app.renderer.resize(dw, dh);
      fire.resize();
      npcs.forEach((npc) => npc.resize());
    };

    // Bufo Bonfire has no ending, so the first move is the only event.
    let hasStarted = false;
    const walk = (direction: Parameters<typeof player.walk>[0]) => {
      if (!hasStarted && !screensaver) {
        hasStarted = true;
        track("Game Start", { game: "Bufo Bonfire" });
      }

      player.walk(direction);
    };

    const handleTouch = (evt: TouchEvent) => {
      if (evt.touches.length < 1 || screensaver) {
        return;
      }

      const { pageX, pageY } = evt.touches[0];

      const dx = pageX - player.container.position.x;
      const dy = pageY - player.container.position.y;

      if (Math.abs(dx) > Math.abs(dy)) {
        walk(dx > 0 ? "right" : "left");
      } else {
        walk(dy > 0 ? "down" : "up");
      }
    };

    const handleKeyPress = (evt: KeyboardEvent) => {
      if (evt.key === "ArrowLeft") {
        walk("left");
      }

      if (evt.key === "ArrowRight") {
        walk("right");
      }

      if (evt.key === "ArrowDown") {
        walk("down");
      }

      if (evt.key === "ArrowUp") {
        walk("up");
      }
    };

    const drawPlayerToFavIcon = () => {
      favIconContext.clearRect(0, 0, favIcon.width, favIcon.height);
      const { texture, direction } = player.getFavIconTexture();

      const source = app.renderer.extract.canvas(texture) as HTMLCanvasElement;

      favIconContext.save();

      if (direction === "right") {
        favIconContext.translate(favIcon.width, 0);
        favIconContext.scale(-1, 1);
      }

      favIconContext.drawImage(source, 0, 0, favIcon.width, favIcon.height);
      favIconContext.restore();

      const url = favIcon.toDataURL("image/png");
      setFavIcon(url);
    };

    const handleTick = () => {
      const fireRect = fire.getHitArea();
      player.update(fireRect);
      npcs.forEach((npc) => npc.update(fireRect));
      drawPlayerToFavIcon();
    };

    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("resize", handleResize);
    window.addEventListener("touchstart", handleTouch);

    app.ticker.add(handleTick);
    handleResize();

    return () => {
      app.ticker.remove(handleTick);
      window.removeEventListener("keyup", handleKeyPress);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("touchstart", handleTouch);
    };
  }, [app, textures]);

  return <div className="GameContainer" ref={domRef} />;
}
